import { ServerRegistry } from './servers'
import { SocketManager } from './socket'
import { computeAverage } from './sort'
import { GraphDisplayManager } from './graph'
import { PercentageBar } from './percbar'
import { FavoritesManager } from './favorites'
import { ServerDetailView } from './server-detail'
import { Tooltip, Caption, formatNumber } from './util'

export class App {
  publicConfig

  constructor () {
    this.tooltip = new Tooltip()
    this.caption = new Caption()
    this.serverRegistry = new ServerRegistry(this)
    this.socketManager = new SocketManager(this)
    this.graphDisplayManager = new GraphDisplayManager(this)
    this.percentageBar = new PercentageBar(this)
    this.favoritesManager = new FavoritesManager(this)
    this.serverDetailView = new ServerDetailView(this)

    this._taskIds = []
  }

  // Called once the DOM is ready and the app can begin setup
  init () {
    this.socketManager.createWebSocket()
  }

  setPageReady (isReady) {
    document.getElementById('push').style.display = isReady ? 'block' : 'none'
    document.getElementById('footer').style.display = isReady ? 'block' : 'none'
    document.getElementById('status-overlay').style.display = isReady ? 'none' : 'block'
  }

  setPublicConfig (publicConfig) {
    this.publicConfig = publicConfig

    this.serverRegistry.assignServers(publicConfig.servers)

    // Start repeating frontend tasks once it has received enough data to be considered active
    // This simplifies management logic at the cost of each task needing to safely handle empty data
    this.initTasks()
  }

  handleSyncComplete () {
    this.caption.hide()

    // Load favorites since all servers are registered
    this.favoritesManager.loadLocalStorage()

    this.percentageBar.redraw()
    this.sortServers()

    // Show global stats button
    document.getElementById('global-stats-toggle').style.display = 'inline-block'

    // The data may not be there to correctly compute values, but run an attempt
    // Otherwise they will be updated by #initTasks
    this.updateGlobalStats()
  }

  initTasks () {
    this._taskIds.push(setInterval(() => this.sortServers(), 10000))
  }

  sortServers () {
    const registrations = this.serverRegistry.getServerRegistrations()
    const sorted = registrations.slice().sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1
      if (b.isFavorite && !a.isFavorite) return 1
      return b.playerCount - a.playerCount
    })

    const parentElement = document.getElementById('server-list')
    for (const reg of sorted) {
      const el = document.getElementById(`container_${reg.serverId}`)
      if (el) {
        parentElement.appendChild(el)
      }
    }
  }

  handleDisconnect () {
    this.tooltip.hide()

    // Hide detail view if open
    this.serverDetailView.hide()

    // Reset individual tracker elements to flush any held data
    this.serverRegistry.reset()
    this.socketManager.reset()
    this.graphDisplayManager.reset()
    this.percentageBar.reset()

    // Undefine publicConfig, resynced during the connection handshake
    this.publicConfig = undefined

    // Clear all task ids, if any
    this._taskIds.forEach(clearInterval)

    this._taskIds = []

    // Reset hidden values created by #updateGlobalStats
    this._lastTotalPlayerCount = undefined
    this._lastServerRegistrationCount = undefined

    // Hide global stats button and panel
    document.getElementById('global-stats-toggle').style.display = 'none'
    document.getElementById('global-stats-panel').style.display = 'none'

    // Reset modified DOM structures
    document.getElementById('stat_totalPlayers').innerText = 0
    document.getElementById('stat_networks').innerText = 0

    this.setPageReady(false)
  }

  getTotalPlayerCount () {
    return this.serverRegistry.getServerRegistrations()
      .map(serverRegistration => serverRegistration.playerCount)
      .reduce((sum, current) => sum + current, 0)
  }

  addServer = (serverId, payload, timestampPoints) => {
    // Even if the backend has never pinged the server, the frontend is promised a placeholder object.
    // result = undefined
    // error = defined with "Waiting" description
    // info = safely defined with configured data
    const serverRegistration = this.serverRegistry.createServerRegistration(serverId)

    serverRegistration.initServerStatus(payload)

    // playerCountHistory is only defined when the backend has previous ping data
    // undefined playerCountHistory means this is a placeholder ping generated by the backend
    if (typeof payload.playerCountHistory !== 'undefined') {
      // Push the historical data into the graph
      // This will trim and format the data so it is ready for the graph to render once init
      serverRegistration.addGraphPoints(payload.playerCountHistory, timestampPoints)

      // Set initial playerCount to the payload's value
      // This will always exist since it is explicitly generated by the backend
      // This is used for any post-add rendering of things like the percentageBar
      serverRegistration.playerCount = payload.playerCount
    }

    // Create the plot instance internally with the restructured and cleaned data
    serverRegistration.buildPlotInstance()

    // Handle the last known state (if any) as an incoming update
    // This triggers the main update pipeline and enables centralized update handling
    serverRegistration.updateServerStatus(payload, this.publicConfig.minecraftVersions)

    // Allow the ServerRegistration to bind any DOM events with app instance context
    serverRegistration.initEventListeners()
  }

  updateGlobalStats = () => {
    // Only redraw when needed
    // These operations are relatively cheap, but the site already does too much rendering
    const totalPlayerCount = this.getTotalPlayerCount()

    if (totalPlayerCount !== this._lastTotalPlayerCount) {
      this._lastTotalPlayerCount = totalPlayerCount
      document.getElementById('stat_totalPlayers').innerText = formatNumber(totalPlayerCount)
    }

    // Only redraw when needed
    // These operations are relatively cheap, but the site already does too much rendering
    const serverRegistrationCount = this.serverRegistry.getServerRegistrations().length

    if (serverRegistrationCount !== this._lastServerRegistrationCount) {
      this._lastServerRegistrationCount = serverRegistrationCount
      document.getElementById('stat_networks').innerText = serverRegistrationCount
    }

    // Update global panel if visible
    const panel = document.getElementById('global-stats-panel')
    if (panel && panel.style.display !== 'none') {
      this.updateGlobalPanelStats()
    }
  }

  updateGlobalPanelStats () {
    const registrations = this.serverRegistry.getServerRegistrations()

    // Total Current Players
    const totalCurrent = this.getTotalPlayerCount()
    document.getElementById('global-stat-current').innerText = formatNumber(totalCurrent)

    // Combined Peak: sum of each server's peak, falling back to max of graph data
    const combinedPeak = registrations.reduce((sum, reg) => {
      if (reg.lastPeakData) {
        return sum + reg.lastPeakData.playerCount
      }
      const counts = reg._graphData[1].filter(v => v !== null && typeof v === 'number')
      return sum + (counts.length > 0 ? Math.max(...counts) : 0)
    }, 0)
    document.getElementById('global-stat-peak').innerText = formatNumber(combinedPeak)

    // Use per-server _graphData (available immediately after sync)
    // All servers share the same timestamp points from the init payload
    const firstReg = registrations[0]
    const graphTimestamps = firstReg ? firstReg._graphData[0] : []

    if (graphTimestamps.length > 0) {
      // Daily Average: at each timestamp index, sum all servers' counts, then average those sums
      const sums = []
      for (let i = 0; i < graphTimestamps.length; i++) {
        let sum = 0
        let hasAny = false
        for (const reg of registrations) {
          const val = reg._graphData[1][i]
          if (val !== null && typeof val === 'number') {
            sum += val
            hasAny = true
          }
        }
        if (hasAny) {
          sums.push(sum)
        }
      }
      const dailyAvg = computeAverage(sums)
      document.getElementById('global-stat-daily-avg').innerText = formatNumber(Math.round(dailyAvg))

      // 72h Average: filter to timestamps >= now - 72h
      const cutoff = Math.floor(Date.now() / 1000) - (72 * 3600)
      const sums72h = []
      for (let i = 0; i < graphTimestamps.length; i++) {
        if (graphTimestamps[i] >= cutoff) {
          let sum = 0
          let hasAny = false
          for (const reg of registrations) {
            const val = reg._graphData[1][i]
            if (val !== null && typeof val === 'number') {
              sum += val
              hasAny = true
            }
          }
          if (hasAny) {
            sums72h.push(sum)
          }
        }
      }
      const avg72h = computeAverage(sums72h)
      document.getElementById('global-stat-72h-avg').innerText = formatNumber(Math.round(avg72h))
    } else {
      document.getElementById('global-stat-daily-avg').innerText = '0'
      document.getElementById('global-stat-72h-avg').innerText = '0'
    }
  }
}
